import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { validatePassword, validateVerificationCode } from "../utils/auth";
import PasswordRequirementsWarning from "../components/PasswordRequirementsWarning";
import signInClient from "../auth";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import Button from "../componentLibrary/Button";

export default function VerificationCodePage() {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const usernameFromQuery = searchParams.get("username");
    const codeFromQuery = searchParams.get("code");

    if (usernameFromQuery) {
      setEmail(usernameFromQuery);
    }

    if (codeFromQuery) {
      setVerificationCode(codeFromQuery);
    }
  }, [searchParams]);

  const handleResetPassword = async () => {
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    const { isError: isErrorCode, errorMessage: codeErrorMessage } =
      validateVerificationCode(verificationCode);

    const { isError: isErrorPassword, errorMessage: passwordErrorMessage } =
      validatePassword(newPassword);

    const errorMessages = [];

    if (isErrorCode) {
      errorMessages.push(codeErrorMessage);
    }

    if (isErrorPassword) {
      errorMessages.push(passwordErrorMessage);
    }

    if (errorMessages.length > 0) {
      setError(errorMessages.join(" "));
      setIsLoading(false);
      return;
    }

    setError("");
    try {
      await signInClient.post("/forgot-password/reset-password", {
        username: email,
        newPassword,
        verificationCode,
      });
      navigate("/login");
      setIsLoading(false);
    } catch {
      setError(
        "There was an error resetting your password. Please verify your username and verification code and try again.",
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col mx-auto w-full max-w-80 px-4 mt-24 mb-12 space-y-4">
      <div className="flex flex-col items-center">
        <img
          src={p4lLogo}
          alt="Psych For Life logo"
          className="w-32 h-32 mb-2"
        />
        <p className="text-gray-700 text-3xl">Psych For Life&reg;</p>
      </div>
      <p className="text-gray-700 text-2xl">Reset Password</p>
      <p>
        You requested a password reset. Enter the 6-digit reset code that was
        sent to your email.
      </p>
      <TextField
        label="Verification Code"
        variant="outlined"
        fullWidth
        name="verificationCode"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value)}
      />
      <TextField
        label="Username"
        variant="outlined"
        fullWidth
        type="username"
        name="username"
        value={email}
        onChange={(e) => {
          setError("");
          setEmail(e.target.value);
        }}
      />
      <div className="space-y-0">
        <TextField
          label="New Password"
          variant="outlined"
          fullWidth
          type={showNewPassword ? "text" : "password"}
          name="newPassword"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setShowPasswordRequirements(e.target.value.length > 0);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <PasswordRequirementsWarning
          password={newPassword}
          showRequirements={showPasswordRequirements}
        />
      </div>
      <TextField
        label="Confirm New Password"
        variant="outlined"
        fullWidth
        type={showConfirmPassword ? "text" : "password"}
        name="confirmPassword"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleResetPassword();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        className="w-full"
        onClick={handleResetPassword}
        loading={isLoading}
      >
        Reset Password
      </Button>
      {error && <p className="text-red-500">{error}</p>}
    </div>
  );
}
